import React, { FC, useState, useRef, useCallback, memo, MouseEvent } from "react";
import AppBar, { AppBarProps } from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GenAppMenu, { AppMenu } from "./GenAppMenu";
import GenButton from "../GenButton/GenButton";

export type Page = AppMenu;

/* eslint-disable-next-line */
export interface GenAppBarProps extends AppBarProps {
  logo?: string;
  siteName: string;
  userName: string;
  pages?: Page[];
  handleLogoClick?: () => void;
  handleLogout?: () => void;
  handleViewProfile?: () => void;
  handleChangeLanguage?: (lng: string) => void;
  isShowAccountIcon?: boolean;
}

const LOGO_PATH = "/static/images/logo_white.png";
const APPBAR_HEIGHT = 64;

interface MobileMenuPageProps {
  page: Page;
  depth?: number;
}

const MobileMenuPage: FC<MobileMenuPageProps> = ({ page, depth = 0 }) => {
  const [open, setOpen] = useState(false);
  const hasSubMenus = page.subMenus && page.subMenus.length > 0;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (hasSubMenus) {
      setOpen((prev) => !prev);
    } else if (page.onClick) {
      page.onClick(e);
    }
  };

  return (
    <>
      <ListItemButton onClick={handleClick} sx={{ pl: 2 + depth * 2 }}>
        <ListItemText primary={page.name} />
        {hasSubMenus ? open ? <ExpandLess /> : <ExpandMore /> : null}
      </ListItemButton>
      {hasSubMenus && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {page.subMenus!.map((sub, i) => (
              <MobileMenuPage key={`${sub.name}-${i}`} page={sub} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const GenAppBar: FC<GenAppBarProps> = (props: GenAppBarProps) => {
  const {
    logo = LOGO_PATH,
    siteName,
    userName,
    color,
    pages,
    handleLogout,
    handleLogoClick,
    handleChangeLanguage,
    isShowAccountIcon = false,
  } = props;
  const [open, setOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenuIndexs, setOpenMenuIndexs] = useState<number[]>([]);
  const [rerenderMenu, setRerenderMenu] = useState(false);
  const anchorEl = useRef<HTMLButtonElement>(null);

  const handleOpenMenu = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const handleCloseMenu = () => {
    setOpen(false);
  };

  const handleOnClickLanguage = (lng: string) => {
    if (handleChangeLanguage) {
      handleChangeLanguage(lng);
    }
  };

  const createMenuNode = useCallback(
    (page: Page, index: number): React.ReactNode => (
      <GenAppMenu
        menu={page}
        key={`${page.name}-${index}`}
        open={openMenuIndexs.includes(index)}
        onToggle={(open) => {
          let openIndexs = openMenuIndexs;
          if (open) {
            openIndexs.filter((i) => i !== index);
          } else {
            openIndexs.push(index);
          }
          setOpenMenuIndexs(openIndexs);
          setRerenderMenu(!rerenderMenu);
        }}
        onClickAway={() => {
          setOpenMenuIndexs([]);
        }}
      />
    ),
    [openMenuIndexs, rerenderMenu],
  );

  return (
    <>
      <AppBar position="fixed" color={color}>
        <Container maxWidth={false}>
          <Toolbar>
            {/* Logo — always visible */}
            <Box
              sx={{
                display: "flex",
                flexGrow: 0,
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Button variant="text" onClick={handleLogoClick}>
                <img src={logo} width={56} height={66} alt="logo" />
              </Button>
              {/* Site name — hidden on mobile */}
              <Box sx={{ margin: "auto 16px", display: { xs: "none", md: "block" } }}>
                <Typography
                  variant="h3"
                  color="white"
                  minWidth={200}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {siteName}
                </Typography>
              </Box>
            </Box>

            {/* Desktop: page links */}
            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
              {pages?.map((page, index) => createMenuNode(page, index))}
            </Box>

            {/* Desktop: user actions */}
            <Box
              sx={{
                display: { xs: "none", md: "inline-flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography color="white">{userName}</Typography>
              {handleChangeLanguage ? (
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  <GenButton
                    variant="text"
                    onClick={() => handleOnClickLanguage("en")}
                    sx={{ color: "white", minWidth: 0 }}
                  >
                    EN
                  </GenButton>
                  {" | "}
                  <GenButton
                    variant="text"
                    onClick={() => handleOnClickLanguage("th")}
                    sx={{ color: "white", minWidth: 0 }}
                  >
                    ไทย
                  </GenButton>
                </Box>
              ) : null}
              <GenButton
                onClick={handleLogout}
                variant="outlined"
                sx={{ borderColor: "white", color: "white" }}
              >
                Log out
              </GenButton>
              {isShowAccountIcon && (
                <>
                  <Tooltip title="Open settings">
                    <Button
                      ref={anchorEl}
                      variant="text"
                      endIcon={<AccountCircleIcon htmlColor="white" />}
                      onClick={handleOpenMenu}
                      sx={{ color: "white" }}
                    >
                      {userName}
                    </Button>
                  </Tooltip>
                  <Menu
                    sx={{ mt: "45px" }}
                    anchorEl={anchorEl.current}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    keepMounted
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={open}
                    onClose={handleCloseMenu}
                  >
                    <MenuItem onClick={handleLogout}>
                      <Typography textAlign="center">Log out</Typography>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>

            {/* Mobile: spacer + language switcher + hamburger button */}
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }} />
            {handleChangeLanguage && (
              <Box sx={{ display: { xs: "inline-flex", md: "none" }, alignItems: "center" }}>
                <GenButton
                  variant="text"
                  onClick={() => handleOnClickLanguage("en")}
                  sx={{ color: "white", minWidth: 0 }}
                >
                  EN
                </GenButton>
                {" | "}
                <GenButton
                  variant="text"
                  onClick={() => handleOnClickLanguage("th")}
                  sx={{ color: "white", minWidth: 0 }}
                >
                  ไทย
                </GenButton>
              </Box>
            )}
            <IconButton
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              sx={{ display: { xs: "flex", md: "none" }, color: "white" }}
              aria-label="open menu"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile dropdown drawer */}
      <Drawer
        anchor="top"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{ display: { xs: "block", md: "none" } }}
        PaperProps={{
          sx: {
            top: `${APPBAR_HEIGHT}px`,
            maxHeight: `calc(100vh - ${APPBAR_HEIGHT}px)`,
            overflowY: "auto",
          },
        }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: { sx: { top: `${APPBAR_HEIGHT}px` } },
        }}
      >
        {/* Section 1: site name, username, logout */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {siteName}
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {userName}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <GenButton
              onClick={() => {
                setMobileMenuOpen(false);
                handleLogout?.();
              }}
              variant="outlined"
              fullWidth
            >
              Log out
            </GenButton>
          </Box>
        </Box>

        {/* Section 2: page links */}
        {pages && pages.length > 0 && (
          <>
            <Divider />
            <List>
              {pages.map((page, i) => (
                <MobileMenuPage key={`${page.name}-${i}`} page={page} />
              ))}
            </List>
          </>
        )}
      </Drawer>
    </>
  );
};

export default memo(GenAppBar);
